// src/pages/AlertsPage.tsx — Phase 1 closer, Slice C commit 3.
//
// Inner-app admin page at /alerts: edit the home page banner.
// Backend: GET /api/alerts (anyone authed) + PUT /api/alerts (admin OR
// alerts-admin, gated server-side via canEditAlerts).
//
// Visual structure ported from docs/prototype/apps2.jsx 134-179 (AlertsApp)
// with the kickoff's structural adjustments: single Card with the form,
// live char counter, last-edited metadata row, and a separate sage-tinted
// success state below the buttons. Inner-app page so BottomNav is hidden
// by MobileFrame.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCurrentUser } from "../auth";
import { apiFetch, ApiError } from "../api";
import { C, F } from "../theme";
import { Button, Card, Eyebrow, Field, PermissionLoading, TopBar } from "../components/ui";

interface AlertResponse {
  text: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

const MAX_CHARS = 150;

export function AlertsPage() {
  const navigate = useNavigate();
  const { me, loading: meLoading } = useCurrentUser();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [initial, setInitial] = useState<string>("");
  const [text, setText] = useState<string>("");
  const [updatedBy, setUpdatedBy] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState<"saved" | "cleared" | null>(null);
  const [confirmingClear, setConfirmingClear] = useState(false);

  const canEdit = Boolean(me?.isAdmin) || Boolean(me?.isAlertsAdmin);

  useEffect(() => {
    let cancelled = false;
    apiFetch<AlertResponse>("/api/alerts")
      .then((data) => {
        if (cancelled) return;
        const t = data.text ?? "";
        setInitial(t);
        setText(t);
        setUpdatedBy(data.updated_by);
        setUpdatedAt(data.updated_at);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : "Could not load alert");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-dismiss success flash after 3 seconds.
  useEffect(() => {
    if (savedFlash === null) return;
    const t = window.setTimeout(() => setSavedFlash(null), 3000);
    return () => window.clearTimeout(t);
  }, [savedFlash]);

  async function save(payloadText: string | null, flash: "saved" | "cleared") {
    setSaving(true);
    setSaveError(null);
    try {
      await apiFetch("/api/alerts", {
        method: "PUT",
        body: JSON.stringify({ alert_text: payloadText }),
      });
      const normalised = payloadText ?? "";
      setInitial(normalised);
      setText(normalised);
      // Updated_by/at refresh: we know it's the current user, but the
      // server has the authoritative timestamp. Re-fetch in the background
      // so the metadata line reflects the new ts without a re-render
      // flicker on the form.
      apiFetch<AlertResponse>("/api/alerts")
        .then((d) => {
          setUpdatedBy(d.updated_by);
          setUpdatedAt(d.updated_at);
        })
        .catch(() => {
          /* keep stale metadata — not critical */
        });
      setSavedFlash(flash);
    } catch (e) {
      setSaveError(e instanceof ApiError ? e.message : "Save failed — please try again");
    } finally {
      setSaving(false);
    }
  }

  // Render gates ---------------------------------------------------------

  if (meLoading) {
    return <PermissionLoading title="Banner Editor" onBack={() => navigate("/apps")} />;
  }
  if (!canEdit) {
    return (
      <div>
        <TopBar
          title="Banner Editor"
          sub="Compose home alert"
          onBack={() => navigate("/apps")}
        />
        <div style={{ padding: "18px 16px" }}>
          <Card>
            <div
              style={{
                fontFamily: F.serif,
                fontSize: 17,
                fontWeight: 700,
                color: C.ink,
                marginBottom: 6,
                letterSpacing: -0.2,
              }}
            >
              Admins only
            </div>
            <p
              style={{
                fontFamily: F.body,
                fontSize: 14,
                color: C.inkSoft,
                fontWeight: 600,
                margin: "0 0 14px",
                lineHeight: 1.5,
              }}
            >
              You don&apos;t have permission to edit alerts.
            </p>
            <Button variant="secondary" onClick={() => navigate("/apps")}>
              Back to apps
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <TopBar
        title="Banner Editor"
        sub="Compose home alert"
        onBack={() => navigate("/apps")}
      />
      <div
        style={{
          padding: "18px 16px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        {loadError && (
          <Card>
            <div style={{ color: C.signalDk, fontFamily: F.body, fontWeight: 700 }}>
              {loadError}
            </div>
          </Card>
        )}

        {loading ? (
          <div
            style={{
              height: 320,
              borderRadius: 18,
              background: C.paperWarm,
              border: `1px solid ${C.hairlineLt}`,
            }}
            aria-hidden="true"
          />
        ) : (
          <Card>
            <Eyebrow>Banner text (max 150 chars)</Eyebrow>
            <Field
              label=""
              value={text}
              onChange={setText}
              rows={3}
              max={MAX_CHARS}
              ph="Type the notice for the home page banner..."
            />
            <div
              style={{
                marginTop: -8,
                fontFamily: F.mono,
                fontSize: 12,
                fontWeight: 700,
                color: text.length > MAX_CHARS ? C.signalDk : C.inkMute,
                letterSpacing: 0.4,
                marginBottom: 12,
              }}
            >
              {text.length} / {MAX_CHARS}
            </div>

            <div
              style={{
                fontFamily: F.serif,
                fontStyle: "italic",
                fontSize: 12.5,
                color: C.inkSoft,
                fontWeight: 600,
                marginBottom: 14,
              }}
            >
              {updatedAt === null
                ? "Never edited"
                : `Last updated by ${updatedBy ?? "—"} on ${formatDate(updatedAt)}`}
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Button
                variant="primary"
                disabled={saving || text === initial}
                onClick={() => save(text.trim() === "" ? null : text.trim(), "saved")}
              >
                {saving ? "Saving..." : "Save banner"}
              </Button>
              <Button
                variant="ghost"
                disabled={saving || initial === ""}
                onClick={() => setConfirmingClear(true)}
              >
                Clear banner
              </Button>
            </div>

            {confirmingClear && (
              <div
                style={{
                  marginTop: 14,
                  padding: 14,
                  borderRadius: 12,
                  background: C.signalBg,
                  border: `1px solid rgba(184,58,43,0.22)`,
                }}
              >
                <div
                  style={{
                    fontFamily: F.serif,
                    fontSize: 15,
                    fontWeight: 700,
                    color: C.signalDk,
                    marginBottom: 4,
                    letterSpacing: -0.1,
                  }}
                >
                  Clear the banner?
                </div>
                <p
                  style={{
                    fontFamily: F.body,
                    fontSize: 13,
                    color: C.inkSoft,
                    fontWeight: 600,
                    margin: "0 0 12px",
                    lineHeight: 1.5,
                  }}
                >
                  This hides the banner. Are you sure?
                </p>
                <div style={{ display: "flex", gap: 10 }}>
                  <Button
                    variant="danger"
                    disabled={saving}
                    onClick={async () => {
                      setConfirmingClear(false);
                      await save(null, "cleared");
                    }}
                  >
                    Yes, clear
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirmingClear(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {saveError && !confirmingClear && (
              <div
                style={{
                  marginTop: 14,
                  padding: 12,
                  borderRadius: 12,
                  background: C.signalBg,
                  border: `1px solid rgba(184,58,43,0.22)`,
                  color: C.signalDk,
                  fontFamily: F.body,
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {saveError}
              </div>
            )}

            {savedFlash && (
              <div
                style={{
                  marginTop: 14,
                  padding: 12,
                  borderRadius: 12,
                  background: C.sageBg,
                  border: `1px solid ${C.sageLt}`,
                  color: C.sageDk,
                  fontFamily: F.body,
                  fontWeight: 800,
                  fontSize: 13,
                  letterSpacing: 0.3,
                }}
                role="status"
              >
                {savedFlash === "saved" ? "Saved" : "Cleared"}
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}
